import React, { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useViewport } from "@/hooks/useViewport";
import CircularButton from "./CircularButton";
import UnifiedActionButton from "./UnifiedActionButton";
import { isCachedBiteChoiceActive } from "./cachedBiteChoice";
import EndLessonButton from "./EndLessonButton";
import SlideProgress from "@/features/tutor/components/SlideProgress.tsx";
import Calculator from "@/features/tutor/components/Calculator";
import { TutorMode } from "@/constants/mode";
import { SCREENS } from "@/features/tutor/constants";
import TooltipWrapper from "@/features/analytics/components/TooltipWrapper";
import { CaretPosition } from "@/types/ui";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useChatAvailability } from "@/features/tutor/hooks/useChatAvailability";
import ChatSurface from "@/features/tutor/components/ChatSurface";
import ModeToggle from "@/features/tutor/components/ModeToggle";
import {
  useNotepadStore,
  selectIsNotepadNonInteractive,
  selectIsNotepadOpen,
} from "@/features/notepad/store/useNotepadStore";

// SVG icons extracted to avoid repetition
const PencilIcon = ({ active = false }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
      fill={active ? "#2C2C3A" : "white"}
    />
  </svg>
);

const CalculatorIconSvg = ({ active = false }) => {
  const stroke = active ? "#2C2C3A" : "white";
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer rounded border */}
      <rect
        x="2"
        y="2"
        width="20"
        height="24"
        rx="3"
        stroke={stroke}
        strokeWidth="1.5"
      />

      {/* Display screen */}
      <rect
        x="4"
        y="5"
        width="16"
        height="4"
        rx="1.5"
        stroke={stroke}
        strokeWidth="1.2"
      />

      {/* Button row 1 */}
      <rect
        x="4"
        y="11"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />
      <rect
        x="10"
        y="11"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />
      <rect
        x="16"
        y="11"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />

      {/* Button row 2 */}
      <rect
        x="4"
        y="16"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />
      <rect
        x="10"
        y="16"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />
      <rect
        x="16"
        y="16"
        width="4"
        height="8"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />

      {/* Button row 3 */}
      <rect
        x="4"
        y="21"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />
      <rect
        x="10"
        y="21"
        width="4"
        height="3"
        rx="0.8"
        stroke={stroke}
        strokeWidth="1"
      />
    </svg>
  );
};

const NOTEPAD_TOOLTIP_CARET = {
  position: CaretPosition.BOTTOM,
  color: "transparent",
};
const NOTEPAD_TOOLTIP_WIDTH = "130px";
const NOTEPAD_TOOLTIP_CLASS =
  "!bg-[#2C2C3A] !text-white !p-2 !shadow-none text-center";

const CALCULATOR_TOOLTIP_CARET = {
  position: CaretPosition.BOTTOM,
  color: "transparent",
};
const CALCULATOR_TOOLTIP_WIDTH = "100px";
const CALCULATOR_TOOLTIP_CLASS =
  "!bg-[#2C2C3A] !text-white !p-2 !shadow-none text-center";

function NotepadButton({
  isCanvasOpen,
  isCanvasAvailable,
  currentScreen,
  onToggleNotepad,
  notepadUsedThisSlide,
  mobileCompact = false,
}) {
  const isNotepadBite = useNotepadStore((s) => s.isNotepadBite);
  if (!isCanvasAvailable) return null;
  if (isNotepadBite) return null;

  const hasContent = notepadUsedThisSlide;
  const isDisabled =
    !isCanvasOpen && (currentScreen !== SCREENS.PROTOTYPE || !hasContent);

  // Mobile: always icon, invert colors when open
  if (mobileCompact) {
    const btn = (
      <button
        onClick={onToggleNotepad}
        disabled={isDisabled}
        className={`flex items-center justify-center transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${
          isDisabled
            ? "opacity-30 pointer-events-none bg-[#2C2C3A]"
            : isCanvasOpen
              ? "bg-white hover:bg-gray-100"
              : "bg-[#2C2C3A] hover:bg-[#262635]"
        }`}
        style={{ width: "48px", height: "48px", borderRadius: "14px" }}
      >
        <NotepadIconSvg active={isCanvasOpen} />
      </button>
    );
    return (
      <div className={isDisabled ? "cursor-not-allowed" : ""}>
        <TooltipWrapper
          trigger={btn}
          tooltip={
            <span>{isCanvasOpen ? "Close Notepad" : "Open Notepad"}</span>
          }
          caret={NOTEPAD_TOOLTIP_CARET}
          width={NOTEPAD_TOOLTIP_WIDTH}
          tooltipClassName={NOTEPAD_TOOLTIP_CLASS}
          lazyRender
        />
      </div>
    );
  }

  // Desktop: text "Close Notepad" when open — no tooltip needed (label already visible)
  if (isCanvasOpen) {
    return (
      <button
        onClick={onToggleNotepad}
        className="flex items-center justify-center transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 bg-white text-[#2C2C3A] px-4"
        style={{ width: "auto", height: "48px", borderRadius: "14px" }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>
          Close Notepad
        </span>
      </button>
    );
  }

  // Desktop: icon-only when closed — show "Open Notepad" tooltip on hover
  return (
    <div className={isDisabled ? "cursor-not-allowed" : ""}>
      <TooltipWrapper
        trigger={
          <button
            onClick={onToggleNotepad}
            disabled={isDisabled}
            className={`flex items-center justify-center transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 bg-[#2C2C3A] ${
              isDisabled
                ? "opacity-30 pointer-events-none"
                : "hover:bg-[#262635]"
            }`}
            style={{ width: "48px", height: "48px", borderRadius: "14px" }}
          >
            <NotepadIconSvg />
          </button>
        }
        tooltip={<span>Open Notepad</span>}
        caret={NOTEPAD_TOOLTIP_CARET}
        width={NOTEPAD_TOOLTIP_WIDTH}
        tooltipClassName={NOTEPAD_TOOLTIP_CLASS}
        lazyRender
      />
    </div>
  );
}

const NotepadIconSvg = ({ active = false }) => {
  const bg = active ? "#2C2C3A" : "white";
  const fg = active ? "white" : "#2C2C3A";
  return (
    <svg
      width="27"
      height="21"
      viewBox="0 0 32 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="32" height="22" rx="5" fill={bg} />
      <path
        d="M17.9189 5.61633C18.223 5.61637 18.4508 5.68488 18.6298 5.78333C18.7179 5.83177 18.7922 5.88705 18.8564 5.94153L19.0214 6.0968L19.0244 6.09973L19.1337 6.22668C19.6356 6.877 19.4781 7.71968 19.0507 8.61145C18.5918 9.56893 17.7874 10.6575 16.9814 11.7736H16.9804C16.6372 12.2416 16.2027 12.8312 15.8388 13.3732C15.6569 13.644 15.4947 13.9015 15.3701 14.1241C15.3204 14.2128 15.2799 14.2952 15.2451 14.3683C15.2805 14.3488 15.3185 14.3269 15.3583 14.3019C15.5334 14.192 15.7379 14.0361 15.954 13.8585C16.3863 13.5035 16.8493 13.0715 17.1943 12.7579H17.1953C17.8465 12.1687 18.4791 11.598 19.0683 11.2238C19.6526 10.8526 20.2395 10.645 20.7783 10.8605C21.1184 10.9966 21.4068 11.2737 21.4872 11.6378C21.6275 12.0587 21.5529 12.5024 21.3837 12.9454C21.2147 13.388 20.9432 13.8511 20.664 14.3165L20.6601 14.3234L20.6552 14.3292C20.5792 14.4306 20.5007 14.5712 20.4238 14.7218C20.5694 14.6015 20.7229 14.4627 20.8652 14.3204C20.9886 14.1671 21.1784 14.077 21.3701 14.0587C21.5702 14.0397 21.7887 14.0965 21.9501 14.2579H21.9492C22.1028 14.3813 22.1926 14.571 22.2109 14.7628C22.2299 14.963 22.1731 15.1815 22.0117 15.3429L21.8652 15.4894L21.8359 15.4318C21.8253 15.4398 21.8132 15.4484 21.8007 15.4581C21.6936 15.5419 21.5334 15.6753 21.3476 15.8097C20.978 16.077 20.4613 16.3829 19.912 16.3829C19.637 16.3829 19.365 16.3145 19.0976 16.1808L19.0839 16.173C18.8388 16.0154 18.6886 15.815 18.623 15.5822C18.5586 15.3532 18.58 15.1074 18.6464 14.8624C18.7779 14.3777 19.1024 13.8445 19.4111 13.3507L19.414 13.3468C19.5351 13.1651 19.6882 12.9051 19.8183 12.6525C19.8692 12.5536 19.9132 12.4574 19.9521 12.3702C19.9328 12.383 19.912 12.3951 19.8915 12.4093C19.7324 12.5195 19.5514 12.6621 19.3622 12.8204C18.9835 13.1373 18.5821 13.508 18.2685 13.7902L18.2665 13.7921C17.5247 14.4412 16.7625 15.1244 16.0517 15.5157C15.695 15.7121 15.338 15.8429 14.9921 15.8527C14.6398 15.8625 14.3091 15.7461 14.0156 15.4698C13.7352 15.2068 13.6103 14.8903 13.6103 14.5363C13.6104 14.1894 13.7297 13.8134 13.9208 13.4191C14.3025 12.6319 14.9995 11.7143 15.7431 10.7228C16.1207 10.1881 16.7526 9.34091 17.2392 8.56848C17.4833 8.181 17.6852 7.82007 17.7997 7.53333C17.8572 7.38933 17.8891 7.27282 17.8964 7.18567C17.8996 7.14815 17.8962 7.12124 17.8925 7.10168C17.7699 7.10719 17.6093 7.15056 17.4111 7.23743C17.2007 7.32961 16.9589 7.46527 16.6923 7.63879C16.1593 7.98586 15.5383 8.47581 14.8867 9.04211C13.5839 10.1743 12.1709 11.6024 11.1152 12.7823L11.1093 12.7882C10.8034 13.0941 10.3454 13.0927 10.041 12.8644L10.0322 12.8575L10.0253 12.8507C9.71946 12.5448 9.72084 12.0868 9.94916 11.7823L9.95404 11.7755L9.95892 11.7697C10.4251 11.2724 11.8321 9.74107 13.4033 8.33411C14.1886 7.63085 15.0183 6.95483 15.7958 6.45422C16.5666 5.95796 17.3115 5.61633 17.9189 5.61633Z"
        fill={fg}
        stroke={fg}
        strokeWidth="0.3"
      />
      <rect x="10" y="23" width="12" height="2" rx="1" fill={bg} />
    </svg>
  );
};

// Left controls: notepad + calculator + slide progress (desktop only)
function LeftControls({
  isCanvasAvailable,
  isCanvasOpen,
  notepadUsedThisSlide,
  currentScreen,
  mode,
  currentSlideIndex,
  slides,
  onToggleNotepad,
}) {
  return (
    <div
      className={`flex items-center gap-2 h-[48px] ${isCanvasOpen ? "max-w-[520px]" : "max-w-[420px]"}`}
    >
      <NotepadButton
        isCanvasOpen={isCanvasOpen}
        isCanvasAvailable={isCanvasAvailable}
        currentScreen={currentScreen}
        onToggleNotepad={onToggleNotepad}
        notepadUsedThisSlide={notepadUsedThisSlide}
      />

      <CalculatorButton />

      <div>
        {mode === TutorMode.PUBLIC && (
          <SlideProgress
            currentSlideIndex={currentSlideIndex}
            slides={slides}
          />
        )}
      </div>

      <ModeToggleSlot />
    </div>
  );
}

function ModeToggleSlot() {
  const { chatAvailable, renderedMode } = useChatAvailability();
  const setInputMode = useChatSettingsStore((s) => s.setInputMode);

  if (!chatAvailable) return null;

  return <ModeToggle mode={renderedMode} onChange={setInputMode} />;
}

function CalculatorButton({ mobileCompact = false }) {
  const isCalculatorAvailable = useUIStore((s) => s.isCalculatorAvailable);
  const isCalculatorOpen = useUIStore((s) => s.isCalculatorOpen);
  const setIsCalculatorOpen = useUIStore((s) => s.setIsCalculatorOpen);

  if (!isCalculatorAvailable) return null;

  const btn = (
    <button
      data-calculator-button
      onClick={() => setIsCalculatorOpen(!isCalculatorOpen)}
      className={`flex items-center justify-center transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 ${
        isCalculatorOpen && mobileCompact
          ? "bg-white hover:bg-gray-100"
          : "bg-[#2C2C3A] hover:bg-[#262635]"
      }`}
      style={{ width: "48px", height: "48px", borderRadius: "14px" }}
    >
      <CalculatorIconSvg active={isCalculatorOpen && mobileCompact} />
    </button>
  );

  return (
    <TooltipWrapper
      trigger={btn}
      tooltip={<span>Calculator</span>}
      caret={CALCULATOR_TOOLTIP_CARET}
      width={CALCULATOR_TOOLTIP_WIDTH}
      tooltipClassName={CALCULATOR_TOOLTIP_CLASS}
      lazyRender
    />
  );
}

// Speak button wrapper
function SpeakButton({ compact = false, stretched = false, ...props }) {
  return (
    <UnifiedActionButton compact={compact} stretched={stretched} {...props} />
  );
}

/**
 * Desktop / tablet / landscape layout: single row [left controls | speak | end lesson + camera]
 */
/**
 * Voice bar or chat bar, whichever the mode calls for. Both are 48px tall and
 * pill-shaped so the toolbar row never changes height across a switch; the
 * widths differ deliberately (the speak button is 300px, a composer cannot be).
 */
export function CentreControl({ speakProps, onSendChat }) {
  const { renderedMode } = useChatAvailability();
  const chatDraft = useRef(null);
  const chat =
    renderedMode === "chat" &&
    !isCachedBiteChoiceActive(speakProps.cachedBitePromptState);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={chat ? "chat" : "voice"}
        initial={{ opacity: 0, rotateX: chat ? -90 : 90 }}
        animate={{ opacity: 1, rotateX: 0 }}
        exit={{ opacity: 0, rotateX: chat ? 90 : -90 }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      >
        {chat ? (
          <ChatSurface onSend={onSendChat} draft={chatDraft} />
        ) : (
          <SpeakButton {...speakProps} />
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function DesktopBottomBar({ leftProps, speakProps, onSendChat }) {
  return (
    <div className="relative px-6 py-4 pb-8 flex items-center justify-between">
      {/* min-w-0 so a wide cluster shrinks instead of forcing the row wider and
          reaching under the centred button. */}
      <div className="flex items-center min-w-0 overflow-hidden">
        <LeftControls {...leftProps} />
      </div>
      {/* Centred on the BAR, not between the flanks.
          This used to sit in flow and rely on `justify-between` with a fixed
          330px right flank — which only centres the button while the LEFT
          cluster happens to be 330px too. Back then it was: 48 (captions) + 48
          (notepad) + 48 (calculator) + 155 (slide progress) + gaps = 323; the
          captions button has since moved onto the tutor blob, leaving ~267.
          That was never a guarantee, and the rest disappear on a notepad bite
          in preview (NotepadButton returns null when isNotepadBite,
          SlideProgress is PUBLIC-only, the calculator is opt-in), collapsing
          the flank to ~56px and dragging the button ~137px left (ENGG-2436).
          Taking it out of flow makes the centring independent of the flanks. */}
      <div
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-end gap-4"
        style={{ perspective: 900 }}
      >
        <CentreControl speakProps={speakProps} onSendChat={onSendChat} />
      </div>
      <div className="flex items-center gap-4 justify-end prevent-select">
        <EndLessonButton />
      </div>
    </div>
  );
}

/**
 * Mobile portrait layout with expand/collapse:
 *   Collapsed: [··· toggle] / [scratchpad?] [speak]
 *   Expanded:  [··· toggle] / [end lesson] / [notepad] [camera] [progress] / [scratchpad?] [speak]
 */
function MobilePortraitBottomBar({ leftProps, speakProps }) {
  const isScratchPadAvailable = useUIStore((s) => s.isScratchPadAvailable);
  const isScratchPadOpen = useUIStore((s) => s.isScratchPadOpen);
  const setIsScratchPadOpen = useUIStore((s) => s.setIsScratchPadOpen);
  const isExpanded = useUIStore((s) => s.isBottomBarExpanded);
  const setIsExpanded = useUIStore((s) => s.setIsBottomBarExpanded);

  const {
    isCanvasAvailable,
    isCanvasOpen,
    notepadUsedThisSlide,
    currentScreen,
    onToggleNotepad,
    mode,
    currentSlideIndex,
    slides,
  } = leftProps;

  return (
    <div className="flex flex-col px-3 pb-6">
      {/* expand/collapse toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-center"
        style={{ height: "32px" }}
        aria-label={isExpanded ? "Collapse controls" : "Expand controls"}
      >
        <svg
          width="20"
          height="10"
          viewBox="0 0 20 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {isExpanded ? (
            <>
              <polyline
                points="2,1 10,7 18,1"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </>
          ) : (
            <>
              <polyline
                points="2,6 10,0 18,6"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </>
          )}
        </svg>
      </button>

      {/* Expanded row — all in one line */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="portrait-expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "visible" }}
          >
            <div className="flex items-center gap-2 pb-2">
              <div className="prevent-select shrink-0">
                <EndLessonButton compact />
              </div>
              <NotepadButton
                isCanvasOpen={isCanvasOpen}
                isCanvasAvailable={isCanvasAvailable}
                currentScreen={currentScreen}
                onToggleNotepad={onToggleNotepad}
                notepadUsedThisSlide={notepadUsedThisSlide}
                mobileCompact
              />
              <CalculatorButton mobileCompact />
              <div className="flex-1 flex justify-center">
                {mode === TutorMode.PUBLIC && (
                  <SlideProgress
                    currentSlideIndex={currentSlideIndex}
                    slides={slides}
                  />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always visible: scratchpad + speak */}
      <div className="flex items-stretch gap-2 h-[48px]">
        <AnimatePresence initial={false}>
          {isScratchPadAvailable && !isCanvasOpen && (
            <motion.div
              key="portrait-scratchpad"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 55, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ overflow: "hidden", flexShrink: 0 }}
            >
              <CircularButton
                icon={<PencilIcon active={isScratchPadOpen} />}
                isActive={isScratchPadOpen}
                onClick={() => setIsScratchPadOpen(!isScratchPadOpen)}
                size="48px"
                className={
                  isScratchPadOpen ? "!bg-white hover:!bg-gray-100" : ""
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex-1 min-w-0">
          <SpeakButton {...speakProps} compact stretched />
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile landscape layout: vertical right sidebar with expand strip on the left.
 *   Collapsed (80px): [16px strip] [64px: scratchpad + speak]
 *   Expanded (144px): [16px strip] [128px: end+notepad+progress+scratchpad+speak]
 */
function MobileLandscapeBottomBar({ leftProps, speakProps }) {
  const isScratchPadAvailable = useUIStore((s) => s.isScratchPadAvailable);
  const isScratchPadOpen = useUIStore((s) => s.isScratchPadOpen);
  const setIsScratchPadOpen = useUIStore((s) => s.setIsScratchPadOpen);
  const isExpanded = useUIStore((s) => s.isBottomBarExpanded);
  const setIsExpanded = useUIStore((s) => s.setIsBottomBarExpanded);

  const {
    isCanvasAvailable,
    isCanvasOpen,
    notepadUsedThisSlide,
    currentScreen,
    onToggleNotepad,
    mode,
    currentSlideIndex,
    slides,
  } = leftProps;

  return (
    <div className="flex flex-row items-stretch bg-[#070717] h-full overflow-hidden">
      {/* Left expand/collapse strip */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="h-full flex items-center justify-center shrink-0"
        style={{ width: "32px" }}
        aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        <svg
          width="10"
          height="20"
          viewBox="0 0 10 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {isExpanded ? (
            <>
              <polyline
                points="1,2 7,10 1,18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </>
          ) : (
            <>
              <polyline
                points="9,2 3,10 9,18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </>
          )}
        </svg>
      </button>

      {/* Extra column — only visible when expanded */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="landscape-expanded"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 171, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "visible" }}
            className="flex flex-col items-center py-4 pr-4 pl-0 gap-3 shrink-0"
          >
            <div className="prevent-select shrink-0">
              <EndLessonButton compact />
            </div>
            <div className="shrink-0">
              <NotepadButton
                isCanvasOpen={isCanvasOpen}
                isCanvasAvailable={isCanvasAvailable}
                currentScreen={currentScreen}
                onToggleNotepad={onToggleNotepad}
                notepadUsedThisSlide={notepadUsedThisSlide}
                mobileCompact
              />
            </div>
            <div className="shrink-0">
              <CalculatorButton mobileCompact />
            </div>
            {mode === TutorMode.PUBLIC && (
              <div className="mt-auto shrink-0">
                <SlideProgress
                  currentSlideIndex={currentSlideIndex}
                  slides={slides}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main column — always visible: scratchpad + speak */}
      <div className="flex flex-col items-center py-4 pl-0 pr-3 gap-3  shrink-0">
        <div className="flex-1 min-h-0 flex flex-col items-center gap-2 w-full justify-end">
          <AnimatePresence initial={false}>
            {isScratchPadAvailable && !isCanvasOpen && (
              <motion.div
                key="landscape-scratchpad"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 55, opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                style={{ overflow: "hidden", flexShrink: 0 }}
              >
                <CircularButton
                  icon={<PencilIcon active={isScratchPadOpen} />}
                  isActive={isScratchPadOpen}
                  onClick={() => setIsScratchPadOpen(!isScratchPadOpen)}
                  size="48px"
                  className={
                    isScratchPadOpen ? "!bg-white hover:!bg-gray-100" : ""
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="flex-1 min-h-[48px] flex items-stretch w-full">
            <SpeakButton {...speakProps} compact stretched />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main BottomBar — selects layout based on viewport.
 */
export default function BottomBar({
  onSendChat,
  // left controls
  isCanvasAvailable,
  isCanvasOpen,
  notepadUsedThisSlide,
  currentScreen,
  mode,
  currentSlideIndex,
  slides,
  onToggleNotepad,
  // speak button
  isPressingToTalk,
  isAwaitingTutorResponse,
  hasPendingWork,
  micUnavailable,
  notepadState,
  showKeyboardGuide,
  onPointerDown,
  onPointerUp,
  cachedBitePromptState,
  onCachedBiteChoice,
  onCachedBiteAttention,
  cachedBiteChoiceReady = true,
}) {
  const { isMobilePortrait, isMobileLandscape } = useViewport();
  const isNotepadNonInteractive = useNotepadStore(
    selectIsNotepadNonInteractive,
  );
  // While the notepad is open with the gateway agent (AI-opened or notepad
  // bite), voice flows over the gateway WS — surface THAT connection state on
  // the speak button instead of the tutor WS, same mechanism as
  // SpaceBottomBar. Otherwise the button flashes "Reconnecting…" whenever the
  // tutor WS blips even though notepad voice is unaffected.
  const isNotepadOpen = useNotepadStore(selectIsNotepadOpen);
  const notepadIngressType = useNotepadStore((s) => s.ingressType);
  const notepadWsConnected = useNotepadStore((s) => s.notepadWs !== null);
  const usesGatewayVoice = isNotepadOpen && notepadIngressType !== "student";
  const isCalculatorAvailable = useUIStore((s) => s.isCalculatorAvailable);
  const isCalculatorOpen = useUIStore((s) => s.isCalculatorOpen);
  const setIsCalculatorOpen = useUIStore((s) => s.setIsCalculatorOpen);

  const leftProps = {
    isCanvasAvailable,
    isCanvasOpen,
    notepadUsedThisSlide,
    currentScreen,
    mode,
    currentSlideIndex,
    slides,
    onToggleNotepad,
  };

  const speakProps = {
    isPressingToTalk,
    isAwaitingTutorResponse,
    hasPendingWork,
    isWsConnected: usesGatewayVoice ? notepadWsConnected : undefined,
    micUnavailable: micUnavailable || isNotepadNonInteractive,
    hideWarningIcon: isNotepadNonInteractive,
    showKeyboardGuide,
    onPointerDown,
    onPointerUp,
    cachedBitePromptState,
    onCachedBiteChoice,
    onCachedBiteAttention,
    cachedBiteChoiceReady,
  };

  if (isMobileLandscape) {
    return (
      <>
        {isCalculatorAvailable && (
          <Calculator
            isOpen={isCalculatorOpen}
            onClose={() => setIsCalculatorOpen(false)}
          />
        )}
        <MobileLandscapeBottomBar
          leftProps={leftProps}
          speakProps={speakProps}
        />
      </>
    );
  }

  return (
    <>
      {isCalculatorAvailable && (
        <Calculator
          isOpen={isCalculatorOpen}
          onClose={() => setIsCalculatorOpen(false)}
        />
      )}
      <div
        id="tutor-bottom-bar"
        className="relative z-[300] bg-[#070717] backdrop-blur-sm"
      >
        {isMobilePortrait ? (
          <MobilePortraitBottomBar
            leftProps={leftProps}
            speakProps={speakProps}
          />
        ) : (
          <DesktopBottomBar
            leftProps={leftProps}
            speakProps={speakProps}
            onSendChat={onSendChat}
          />
        )}
      </div>
    </>
  );
}
